import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { ReactElement } from 'react'
import { SectionTitle } from 'app/components/ai-agent-voice-ops/SectionTitle'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import SegmentControl from 'app/components/viz/SegmentControl'
import type { FailedReplyFilter } from 'app/types/sms-agent-ops'
import { DataSourceBadge } from './DataSourceBadge'
import { ListErrorState } from './ListErrorState'
import { FAILED_REPLY_LABEL, FAILED_REPLY_STATUSES } from './smsFailedReplies'
import type { UseSmsAgentOpsReturn } from './useSmsAgentOps'

const FILTER_OPTIONS: { value: FailedReplyFilter; label: string }[] = [
  { value: `all`, label: `All` },
  ...FAILED_REPLY_STATUSES.map((status) => ({ value: status, label: FAILED_REPLY_LABEL[status] })),
]

type FailedRepliesSectionProps = Pick<
  UseSmsAgentOpsReturn,
  | `listErrors`
  | `failedReplies`
  | `failedReplyCounts`
  | `failedReplyFilter`
  | `setFailedReplyFilter`
  | `failedReplyColumns`
  | `failedRepliesRef`
>

/**
 * Failed AI Replies (plan D39): patient texts the AI tried to answer and could not, with the
 * reason the system recorded (TRK-5908). Not answered rows are still owed a reply; Answered
 * later rows got one on a retry or a later turn.
 */
export function FailedRepliesSection({
  listErrors,
  failedReplies,
  failedReplyCounts,
  failedReplyFilter,
  setFailedReplyFilter,
  failedReplyColumns,
  failedRepliesRef,
}: FailedRepliesSectionProps): ReactElement {
  return (
    <div ref={failedRepliesRef} className="mb-8 scroll-mt-4">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-y-2">
        <SectionTitle
          title="Failed AI Replies"
          badge={`${failedReplyCounts.all} Texts`}
          adornment={<DataSourceBadge source="live" />}
        />
        {/* Scrolls on its own on a phone rather than pushing the page sideways. */}
        <div className="max-w-full overflow-x-auto">
          <SegmentControl
            label="Status"
            buttons={FILTER_OPTIONS.map((option) => ({
              label: `${option.label} ${failedReplyCounts[option.value]}`,
              onClick: () => setFailedReplyFilter(option.value),
              isActive: failedReplyFilter === option.value,
            }))}
          />
        </div>
      </div>
      <p className="mb-3 text-body-sm text-subtle">
        Patient texts in the date range the AI tried to answer and could not, with the reason. A failed reply is retried
        automatically; Answered later means a retry or a later reply got through.
      </p>
      <SimpleTable
        activeSubpage="sms-agent-ops-failed-replies"
        data={failedReplies}
        columns={failedReplyColumns}
        enableDeepLinking={false}
        hideFooter
        emptyState={
          listErrors.failedReplies ? (
            <ListErrorState subject="failed AI replies" />
          ) : (
            <TableEmptyState
              icon={<ExclamationTriangleIcon className="h-6 w-6" />}
              title="No failed AI replies"
              description="Every AI reply attempt in the date range went through."
            />
          )
        }
      />
    </div>
  )
}
